import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import CHorizontalScrollContainer from '../../components/CHorizontalScrollContainer';
import CLocalizedText from '../../components/CLocalizedText';
import CPageModule from '../../components/CPageModule';
import CPageModulePlot from '../../components/CPageModulePlot';
import CPandasTable from '../../components/CPandasTable';
import CStatefulContainer from '../../components/CStatefulContainer';
import GenericDateRangeSelector from '../../components/GenericDateRangeSelector';
import { updateState, updateStateAttribute, useStateExtended } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import { type IResponseTableOrPlot } from '../../models/IResponseTableOrPlot';
import { type PlotlyResponse } from '../../models/PlotlyModel';
import { type TPandasTable } from '../../models/TPandasTable';
import PublicFactsheetServices from '../../services/publicFactsheetServices';
import CTabButtonWrapper from '../../components/CTabButtonWrapper';
import CTabButton from '../../components/CTabButton';
import CLocalizedTextString from '../../components/CLocalizedTextString';

interface Props {
  defaultStartDate: string;
  defaultEndDate?: string;
}

interface State {
  startDateString: string;
  endDateString: string;
  plot: IResponseTableOrPlot<PlotlyResponse>;
  table: IResponseTableOrPlot<TPandasTable>;
}

export default function CPerformancePlotAndTable(props: Props) {
  const defaultStartDate = new Date(props.defaultStartDate).getTime();
  const defaultEndDateObj = new Date();
  defaultEndDateObj.setHours(0, 0, 0, 0);
  const defaultEndDate = new Date(props.defaultEndDate ?? defaultEndDateObj).getTime();

  const { factsheetId } = useParams();
  const { locale } = useAppSelector((state) => state.dashboard);

  const [state, setState, getState] = useStateExtended<State>({
    plot: {
      state: {
        isLoading: true,
        isError: false
      },
      params: {},
      data: undefined
    },
    table: {
      state: {
        isLoading: true,
        isError: false
      },
      params: {},
      data: undefined
    },
    startDateString: dayjs(defaultStartDate).format('YYYY-MM-DD'),
    endDateString: dayjs(defaultEndDate).format('YYYY-MM-DD'),
  });

  const fetchPlot = async (startDate: string, endDate: string) => {
    const latestState = (await getState()).plot;
    updateState<State>(
      {
        plot: {
          ...latestState,
          state: { ...latestState.state, isLoading: true }
        },
      },
      state,
      setState
    );
    const res = await PublicFactsheetServices.getPerformancePlot(factsheetId ?? '', startDate, endDate);
    if (res.wasSuccessful()) {
      updateState<State>(
        {
          plot: {
            ...latestState,
            state: { isLoading: false, isError: false },
            data: res.getData()
          },
        },
        state,
        setState
      );
    } else {
      updateState<State>(
        {
          plot: {
            ...latestState,
            state: { isLoading: false, isError: true, strError: res.getErrorString(), objError: res.getErrorObj() }
          },
        },
        state,
        setState
      );
    }
  };

  const fetchTable = async (startDate: string, endDate: string) => {
    const latestState = (await getState()).table;
    updateState<State>(
      {
        table: {
          ...latestState,
          state: { ...latestState.state, isLoading: true }
        },
      },
      state,
      setState
    );
    const res = await PublicFactsheetServices.getPerformanceTable(factsheetId ?? '', startDate, endDate);
    if (res.wasSuccessful()) {
      updateState<State>(
        {
          table: {
            ...latestState,
            state: { isLoading: false, isError: false },
            data: res.getData()
          },
        },
        state,
        setState
      );
    } else {
      updateState<State>(
        {
          table: {
            ...latestState,
            state: { isLoading: false, isError: true, strError: res.getErrorString(), objError: res.getErrorObj() }
          },
        },
        state,
        setState
      );
    }
  };

  const handleDateRangeChange = async (startDate: number, endDate: number) => {
    const startDateString = dayjs(startDate).format('YYYY-MM-DD');
    const endDateString = dayjs(endDate).format('YYYY-MM-DD');
    updateStateAttribute<State>({startDateString, endDateString}, setState);
    await Promise.all([
      fetchPlot(startDateString, endDateString),
      fetchTable(startDateString, endDateString),
    ]);
  };

  useEffect(() => {
    (async () => {
      await handleDateRangeChange(defaultStartDate, defaultEndDate);
    })();
  }, [locale, props.defaultStartDate]);

  return (
    <>
      <CPageModule submodule>
        <div className={'flex justify-end flex-wrap gap-4 items-center'}>
          <div className={'flex-grow'}>
            <CTabButtonWrapper>
              <CTabButton isSelected={state.startDateString === '2024-05-22'} label={'sinceAlloc'} onClick={async () => {
                await handleDateRangeChange(new Date('2024-05-22').getTime(), new Date(state.endDateString).getTime());
              }} text={CLocalizedTextString('fsperfP2tab1')} />
              <CTabButton isSelected={state.startDateString === '2024-05-06'} label={'sinceLaunch'} onClick={async () => {
                await handleDateRangeChange(new Date('2024-05-06').getTime(), new Date(state.endDateString).getTime());
              }} text={CLocalizedTextString('fsperfP2tab2')} />
            </CTabButtonWrapper>
          </div>
          <GenericDateRangeSelector
            onChange={handleDateRangeChange}
            initStartDate={new Date(state.startDateString).getTime()}
            initEndDate={new Date(state.endDateString).getTime()}
            minDate={new Date('2024-05-06').getTime()}
            maxDate={defaultEndDate}
            minDateSpan={10}
          />
        </div>
      </CPageModule>
      <CStatefulContainer submodule showContent={!!state.plot.data} apiRequestState={state.plot.state}>
        <CPageModulePlot data={state.plot.data?.data} layout={state.plot.data?.layout} />
      </CStatefulContainer>
      <CPageModule submodule>
        <p>
          <CLocalizedText dictKey={'fsperfP1Desc2'} />
        </p>
      </CPageModule>
      <CStatefulContainer submodule showContent={!!state.table.data} apiRequestState={state.table.state}>
        <CHorizontalScrollContainer>
          <CPandasTable
            data={state.table.data}
            colSpanArr={[1, 1, 1, 1, 1, 1]}
            colAlignmentArr={[undefined, 'right', 'right', 'right', 'right', 'right']}
          />
        </CHorizontalScrollContainer>
      </CStatefulContainer>
    </>
  );
}
